import React, { useEffect, useState } from "react";
import DialogBox from "@/components/Common/DialogBox";
import Loader from "@/components/Loader";
import { marketingHistoryService } from "@/services/marketingHistoryService";

const RECIPIENT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone Number" },
  { key: "status", label: "Status" },
];

const isSentStatus = (status) =>
  status === "sent" || status === "queued" || status === "delivered";

const normalizeRecipient = (item, index) => {
  const status = item.status ?? "";
  return {
    id: item.id ?? item.candidate_id ?? item.user_id ?? `recipient-${index}`,
    name: item.name ?? item.candidate_name ?? item.user?.name ?? "-",
    email: item.email ?? item.candidate_email ?? item.user?.email ?? "-",
    phone_number:
      item.phone_number ?? item.candidate_phone ?? item.user?.phone_number ?? "-",
    status,
    sent: isSentStatus(status),
  };
};

const extractRecipients = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.recipients)) return response.recipients;
  if (Array.isArray(response)) return response;
  return [];
};

const MarketingHistoryRecipientsModal = ({
  isOpen,
  onClose,
  batchId,
  batchName,
}) => {
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !batchId) return;

    const loadRecipients = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await marketingHistoryService.getBatchRecipients(batchId);
        setRecipients(extractRecipients(response).map(normalizeRecipient));
      } catch (err) {
        setError(err.message || "Failed to load recipients");
        setRecipients([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecipients();
  }, [isOpen, batchId]);

  const handleClose = () => {
    setRecipients([]);
    setError(null);
    onClose?.();
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={handleClose}
      title={`Recipients — ${batchName || "Campaign"}`}
      className="w-[50%] max-md:w-[95%]"
      showIcon={false}
    >
      {isLoading ? (
        <div className="py-10 flex justify-center">
          <Loader bgColor="bg-transparent" loadingText="Loading recipients..." />
        </div>
      ) : error ? (
        <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>
      ) : recipients.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">
          No recipients found for this run.
        </p>
      ) : (
        <div className="overflow-x-auto max-h-[50vh]">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted sticky top-0">
              <tr>
                {RECIPIENT_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {recipients.map((recipient) => (
                <tr key={recipient.id} className="hover:bg-muted">
                  <td className="px-4 py-3 text-sm text-foreground">
                    {recipient.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground break-all">
                    {recipient.email}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground whitespace-nowrap">
                    {recipient.phone_number}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        recipient.sent
                          ? "bg-green-500/10 text-green-600"
                          : "bg-red-500/10 text-red-600"
                      }`}
                    >
                      {recipient.sent ? "Sent" : "Not Sent"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DialogBox>
  );
};

export default MarketingHistoryRecipientsModal;
